import { ref } from "vue";
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";

import { useAuthStore } from "@/stores/Auth";
import { goFetch, isOkStatus } from "@/utils/network-utils";
import type { ApiFetchResponse, PersistedQueryPayload } from "@tmi/types/api/fetch";
import { clientStateManager } from "@/services/error-services";
import { SellSignal } from "@tmi/types/entities";
import { SellSignalApiResponse } from "@tmi/types/api/sell-signal";
import { FiveMinutesInMilliseconds } from "@/constants";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

/**
 * UX roadmap #63: how long a load of the Trade of the Day history satisfies later calls. The store used to latch on the
 * first success for the tab's lifetime, through a sign-out and the next member's session. After this window the next
 * call still resolves at once with the list shown and refreshes it behind.
 */
export const SELL_SIGNALS_FRESH_MS = FiveMinutesInMilliseconds;

const error = ref< string | undefined >();
const loading = ref< boolean >( false );
// When the last successful load landed (Date.now()), or null when nothing is loaded for this session.
let loadedAtMs: number | null = null;
// The load in flight, so concurrent callers wait for it instead of returning before its data lands.
let inFlightLoad: Promise< void > | null = null;
// Bumped by reset(): a load started for the previous session must not apply its response to the next one.
let loadGeneration: number = 0;

const allSellSignals = ref< SellSignal[] >( [] );

export const useSellSignalsStore = defineStore( "sell-signals", () => {
    // Depends on authStore for authentication state
    const { authToken } = storeToRefs( useAuthStore() );

    // Clear all internal tracking and state variables
    function reset() {
        error.value = undefined;
        loading.value = false;
        loadedAtMs = null;
        inFlightLoad = null;
        loadGeneration += 1;

        allSellSignals.value = [] as SellSignal[];
    }

    async function load( generation: number ): Promise< void > {
        loading.value = true;

        try {
            const response: ApiFetchResponse< SellSignalApiResponse > | null = await goFetch< SellSignalApiResponse, PersistedQueryPayload >( {
                serverBaseUri: `${ import.meta.env.VITE_SENTIENT_BASE_URI }`,
                endpoint: `${ import.meta.env.VITE_SENTIENT_REL_PATH }`,
                authToken: authToken.value,
                payload: {
                    id: 'b6adbf34898016b2b6f519fa60f09b2d'
                }
            } );

            // Signed out (or in as someone else) while this was in flight: the answer was for that session.
            if ( generation !== loadGeneration ) {
                return;
            }

            // If API call failed (returned null), don't reset - just skip processing
            if ( !response ) {
                logger.warn( 'Sell signals API call failed, will not retry immediately' );
            } else if ( isOkStatus( response.status ) && response.data?.tradeOfDayHistory ) {
                const sellSignalData = response.data.tradeOfDayHistory;
                allSellSignals.value = [ ...( sellSignalData.history ) ];
                loadedAtMs = Date.now();
            }
        } catch ( err: any ) {
            if ( generation !== loadGeneration ) {
                return;
            }

            // If anything went wrong while attempting to initialize the store, reset it and then handle the specific error.
            // A refresh behind a list already shown keeps that list.
            if ( null === loadedAtMs ) {
                reset();
            }

            clientStateManager.queueClientError( err, {
                token: authToken.value,
                request: 'sell-signals'
            } );
        } finally {
            if ( generation === loadGeneration ) {
                loading.value = false;
                inFlightLoad = null;
            }
        }
    }

    /**
     * Loads the history unless a fresh copy is in (SELL_SIGNALS_FRESH_MS). A stale copy resolves at once and refreshes
     * behind; with nothing loaded, callers wait for the load, joining one already in flight. Never rejects.
     */
    async function initializeAsync(): Promise< void > {
        if ( !authToken.value ) {
            return;
        }

        const fresh: boolean = null !== loadedAtMs && Date.now() - loadedAtMs < SELL_SIGNALS_FRESH_MS;
        if ( fresh ) {
            logger.debug( '[ Sell-Signals ].initialize() exiting - already loaded' );
            return;
        }

        if ( !inFlightLoad ) {
            inFlightLoad = load( loadGeneration );
        }

        // Stale-while-revalidate: the list on screen stays while it refreshes.
        if ( null === loadedAtMs ) {
            await inFlightLoad;
        }
    }

    return {
        // ACTIONS
        initializeAsync,
        reset,

        // GETTERS
        allSellSignals
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useSellSignalsStore, import.meta.hot ) );
}